import { Injectable } from '@angular/core';

/**
 * -------------------------------------------------------
 * Global Razorpay type declaration
 * -------------------------------------------------------
 * Razorpay SDK attaches itself to `window.Razorpay`.
 * We declare it here so TypeScript understands it.
 */
declare global {
    interface Window {
        Razorpay?: new (options: any) => {
            open: () => void;
            on: (event: string, cb: (res: any) => void) => void;
            close?: () => void;
        };
    }
}

/**
 * -------------------------------------------------------
 * Callback type definitions
 * -------------------------------------------------------
 */
export type RazorpaySuccessHandler = (response: any) => void;
export type RazorpayFailureHandler = (error: any) => void;

/**
 * -------------------------------------------------------
 * Payload contract for opening Razorpay checkout
 * -------------------------------------------------------
 * Everything required to safely open the Razorpay modal
 * is passed through this interface.
 */
export interface RazorpayOpenPayload {

    /** Order details returned from backend */
    order: {
        id: string;                  // Razorpay order_id
        amount: number;              // Amount in subunits (paise)
        currency: string;            // INR
        razorpay_key: string;        // Key ID (test/live)
        notes?: {
            payment_for?: string;      // Optional description
            [k: string]: any;
        };
    };

    /** Customer information */
    customer: {
        name: string;
        mobile: string;
        email?: string;
    };

    /** Callbacks */
    onSuccess: RazorpaySuccessHandler;
    onFailure: RazorpayFailureHandler;

    /** Optional UI customization */
    themeColor?: string;
    extraOptions?: Record<string, any>;
}

/**
 * -------------------------------------------------------
 * Razorpay Service
 * -------------------------------------------------------
 * - Loads Razorpay SDK only once
 * - Opens checkout safely
 * - Ensures success/failure callbacks fire ONLY ONCE
 */
@Injectable({ providedIn: 'root' })
export class RazorpayService {

    /** Script metadata */
    private readonly scriptId = 'razorpay-checkout-js';
    private readonly scriptSrc = 'https://checkout.razorpay.com/v1/checkout.js';

    /** Cached promise to prevent duplicate loads */
    private loadPromise?: Promise<boolean>;

    /**
     * -------------------------------------------------------
     * Load Razorpay SDK dynamically
     * -------------------------------------------------------
     * - Prevents duplicate script loading
     * - Reuses the same promise across calls
     */
    load(): Promise<boolean> {

        // SDK already available
        if (window.Razorpay) {
            return Promise.resolve(true);
        }

        // Already loading → return same promise
        if (this.loadPromise) {
            return this.loadPromise;
        }

        // Script already present in DOM
        const existing = document.getElementById(this.scriptId) as HTMLScriptElement | null;
        if (existing) {
            this.loadPromise = new Promise<boolean>((resolve) => {
                if (window.Razorpay) return resolve(true);
                existing.addEventListener('load', () => resolve(true), { once: true });
                existing.addEventListener('error', () => resolve(false), { once: true });
            });
            return this.loadPromise;
        }

        // Fresh script injection
        this.loadPromise = new Promise<boolean>((resolve) => {
            const script = document.createElement('script');
            script.id = this.scriptId;
            script.src = this.scriptSrc;
            script.async = true;

            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);

            document.body.appendChild(script);
        });

        return this.loadPromise;
    }

    /**
     * -------------------------------------------------------
     * Create Razorpay instance
     * -------------------------------------------------------
     * Ensures SDK is loaded before instantiation.
     */
    private async createInstance(options: any) {
        const ok = await this.load();
        if (!ok || !window.Razorpay) {
            throw new Error('Razorpay SDK failed to load');
        }
        return new window.Razorpay(options);
    }

    /**
     * -------------------------------------------------------
     * Build Razorpay checkout options
     * -------------------------------------------------------
     * Important:
     * - Razorpay may fire multiple events (success + failed)
     * - We guard callbacks so ONLY ONE executes
     */
    private buildOptions(payload: RazorpayOpenPayload) {
        const { order, customer, themeColor, extraOptions } = payload;

        let completed = false; // 🔐 callback guard

        /** Ensures success fires only once */
        const successOnce = (res: any) => {
            if (completed) return;
            completed = true;
            payload.onSuccess(res);
        };

        /** Ensures failure fires only once */
        const failureOnce = (err: any) => {
            if (completed) return;
            completed = true;
            payload.onFailure(err);
        };

        const options: any = {
            key: order.razorpay_key,
            amount: order.amount,
            currency: order.currency,
            name: customer.name,
            description: order.notes?.payment_for || 'Ticket Booking Payment',
            order_id: order.id,

            /** Brand logo */
            image: 'https://kisan-common.s3.ap-south-1.amazonaws.com/kisan-in/images/kisanlogo.png',

            /** Razorpay success handler */
            handler: (response: any) => successOnce(response),

            /** Modal close handler */
            modal: {
                ondismiss: () => failureOnce({ error: 'Payment popup closed.' }),
            },

            /** Autofill user details */
            prefill: {
                name: customer.name,
                contact: customer.mobile,
                email: customer.email,
            },

            /** Theme customization */
            theme: {
                color: themeColor ?? '#008940',
            },

            /** Allow caller to extend Razorpay options */
            ...(extraOptions ?? {}),
        };

        /**
         * Attach guarded handlers so they can be reused
         * from payment.failed & catch blocks
         */
        options.__successOnce = successOnce;
        options.__failureOnce = failureOnce;

        return options;
    }

    /**
     * -------------------------------------------------------
     * Open Razorpay Checkout
     * -------------------------------------------------------
     * Handles:
     * - SDK loading
     * - payment.failed event
     * - popup close
     * - unexpected JS errors
     */
    async openCheckout(payload: RazorpayOpenPayload): Promise<void> {
        const options = this.buildOptions(payload);

        try {
            const rzp = await this.createInstance(options);

            /**
             * Razorpay can emit payment.failed
             * after popup close → guard ensures single callback
             */
            rzp.on?.('payment.failed', (res: any) => {
                options.__failureOnce(res);
            });

            // Open Razorpay modal
            rzp.open();

        } catch (e: any) {
            // SDK load / init failure
            options.__failureOnce({
                error: e?.message || 'Unable to open Razorpay checkout.'
            });
        }
    }
}
